import React, { EventHandler, useCallback, useEffect, useState } from 'react';
import {
  AssemblyType,
  AssemblyTypeKey,
  BackflowAssemblyInfo,
  InstallationStatus,
  JobFormDataInput,
} from '@ecosconnect/core/src/types/jobFormData';
import AccordionItem from '../../Accordion/AccordionItem';
import { Box } from 'rebass';
import BackflowAssemblyMakePicker from '../../BackflowAssembly/BackflowAssemblyMakePicker';
import useMakesAndModels from '../../../hooks/useMakesAndModels';
import BackflowAssemblyModelPicker from '../../BackflowAssembly/BackflowAssemblyModelPicker';
import { Input, Select } from '../../Form';
import { Textarea } from '@rebass/forms';
import { assemblySizes } from '../../BackflowAssembly/options';
import { SetFormValue, SetFormValues } from '../../Job/JobThirdPane';
import { toNumber } from '@ecosconnect/core/src/helpers/number';

type BackflowTestAssemblyValuesEditProps = {
  formData: JobFormDataInput;
  setAssemblyValues: (updates: Partial<BackflowAssemblyInfo>) => void;
  setFormValue: SetFormValue;
};

const BackflowTestAssemblyValuesEdit: React.FC<
  BackflowTestAssemblyValuesEditProps
> = ({ formData, setAssemblyValues, setFormValue }) => {
  const { makesByModel } = useMakesAndModels();
  const [selectedMakeId, setSelectedMakeId] = useState<string>();
  const { backflowAssembly } = formData;

  const assemblyChangeHandler = useCallback<EventHandler<any>>(
    (e) => {
      if (!e.target.name) {
        throw new Error(`${e.target.tagName} element does not have a name`);
      }
      // type and size are numeric in JobFormData; select values arrive as strings
      const value = ['type', 'size'].includes(e.target.name)
        ? toNumber(e.target.value)
        : e.target.value;
      setAssemblyValues({ [e.target.name]: value });
    },
    [setAssemblyValues]
  );

  useEffect(() => {
    if (
      makesByModel &&
      backflowAssembly?.modelId &&
      makesByModel[backflowAssembly.modelId]
    ) {
      setSelectedMakeId(makesByModel[backflowAssembly.modelId].id);
    }
  }, [makesByModel, backflowAssembly?.modelId, setSelectedMakeId]);

  if (
    !backflowAssembly ||
    formData.installationStatus === InstallationStatus.REMOVED
  ) {
    return <></>;
  }

  return (
    <>
      <AccordionItem
        label="Make"
        alignItems="center"
        as="label"
        value={
          <Box my={-1}>
            <BackflowAssemblyMakePicker
              makeId={selectedMakeId}
              onChange={(selected) => {
                setSelectedMakeId(selected?.value);
                setAssemblyValues({
                  makeName: selected?.label.toString(),
                  modelName: '',
                });
              }}
            />
          </Box>
        }
      />
      <AccordionItem
        label="Model"
        alignItems="center"
        as="label"
        value={
          <Box my={-1}>
            <BackflowAssemblyModelPicker
              makeId={selectedMakeId}
              modelId={backflowAssembly.modelId + ''}
              onChange={(option) => {
                setAssemblyValues({
                  modelId: toNumber(option?.value),
                  modelName: option?.label.toString(),
                });
                // setAssemblyInput((prevState) => ({
                //   ...prevState,
                //   modelId: value?.value,
                // }))
              }}
            />
          </Box>
        }
      />
      <AccordionItem
        label="Serial"
        alignItems="center"
        as="label"
        value={
          <Input
            onChange={assemblyChangeHandler}
            variant="accordion"
            width="100px"
            name="serialNumber"
            value={backflowAssembly.serialNumber ?? undefined}
            autoComplete="off"
          />
        }
      />
      <AccordionItem
        label="Type"
        alignItems="center"
        as="label"
        value={
          <Select
            name="type"
            variant="accordion"
            onChange={assemblyChangeHandler}
            value={backflowAssembly.type}
            width="100%"
          >
            {!backflowAssembly.type && <option value="">Select Type</option>}
            {Object.keys(AssemblyType).map((type, i) => (
              <option key={i} value={AssemblyType[type as AssemblyTypeKey]}>
                {type.replace(/_/g, ' ')}
              </option>
            ))}
          </Select>
        }
      />
      <AccordionItem
        label="Size"
        alignItems="center"
        as="label"
        value={
          <Select
            name="size"
            variant="accordion"
            onChange={assemblyChangeHandler}
            value={backflowAssembly.size ?? undefined}
            width="100%"
          >
            {!backflowAssembly.size && (
              <option value={undefined}>Select Size</option>
            )}
            {assemblySizes.map((size, i) => (
              <option value={size.decimal} key={i}>
                {size.fraction}
              </option>
            ))}
          </Select>
        }
      />
      <AccordionItem label="Location" as="label" htmlFor="location" value=" " />
      <Textarea
        id="location"
        onChange={assemblyChangeHandler}
        name="location"
        value={backflowAssembly.location ?? undefined}
        height={54}
      />
      {/* <AccordionItem
        label="Install Date"
        as="label"
        htmlFor="installDate"
        value=" "
      />
      <Input
        name="installDate"
        onChange={assemblyChangeHandler}
        type="date"
        value={backflowAssembly.installDate ?? undefined}
        autoComplete="off"
      /> */}
    </>
  );
};

export default BackflowTestAssemblyValuesEdit;
